import { useState, useCallback, useMemo } from 'react';
import { useGameContext } from '../contexts/GameContext';
import { PreviewTargetSelect } from './PreviewTargetSelect';
import {
  EffectType,
  EffectMapping,
  GameEffectSettings,
  BorderGlowEffectConfig,
  ConfettiEffectConfig,
  TsParticlesEffectConfig,
  ConfettiPreset,
  getEventsForGameType,
  getEventDisplayName,
  getEventIcon,
  getEffectTypeDisplayName,
  getAllEffectTypes,
  createEffectMapping,
  isTsParticlesEffectType,
  EffectEvent,
  AdminPreviewEffectRequest,
} from '@swtorbit/shared-socket-events';

/**
 * EffectsSettings - Admin configuration panel for visual effects
 * 
 * Uses the event-to-effect mapping system where admins can:
 * - Add/remove effect mappings
 * - Choose which event triggers which effect type
 * - Configure each effect's settings
 */
export function EffectsSettings() {
  const { selectedGame, updateEffectSettings, previewEffect } = useGameContext();
  const [addingMapping, setAddingMapping] = useState(false);
  const [newMappingEvent, setNewMappingEvent] = useState<EffectEvent | ''>('');
  const [newMappingEffectType, setNewMappingEffectType] = useState<EffectType>(EffectType.BORDER_GLOW);
  const [previewTargetPlayerId, setPreviewTargetPlayerId] = useState<string>('');

  // Get effect settings from game, or empty default
  const effectSettings: GameEffectSettings = useMemo(() => {
    return selectedGame?.effectSettings || { mappings: [] };
  }, [selectedGame?.effectSettings]);
  
  // Get available events for this game type
  const availableEvents = useMemo(() => {
    if (!selectedGame) return [];
    return getEventsForGameType(selectedGame.gameType);
  }, [selectedGame?.gameType]);
  
  // Filter out events that already have mappings
  const unusedEvents = useMemo(() => {
    const usedEvents = new Set(effectSettings.mappings.map(m => m.event));
    return availableEvents.filter(e => !usedEvents.has(e));
  }, [availableEvents, effectSettings.mappings]);
  
  // Handle adding a new mapping
  const handleAddMapping = useCallback(() => {
    if (!newMappingEvent || !selectedGame) return;
    
    const newMapping = createEffectMapping(newMappingEvent, newMappingEffectType);
    const newSettings: GameEffectSettings = {
      mappings: [...effectSettings.mappings, newMapping],
    };
    
    // Emit socket event to update settings
    updateEffectSettings({
      roomCode: selectedGame.roomCode,
      effectSettings: newSettings,
    });
    
    // Reset form
    setAddingMapping(false);
    setNewMappingEvent('');
    setNewMappingEffectType(EffectType.BORDER_GLOW);
  }, [newMappingEvent, newMappingEffectType, selectedGame, effectSettings, updateEffectSettings]);
  
  // Handle removing a mapping
  const handleRemoveMapping = useCallback((mappingId: string) => {
    if (!selectedGame) return;
    
    const newSettings: GameEffectSettings = {
      mappings: effectSettings.mappings.filter(m => m.id !== mappingId),
    };
    
    // Emit socket event to update settings
    updateEffectSettings({
      roomCode: selectedGame.roomCode,
      effectSettings: newSettings,
    });
  }, [effectSettings, selectedGame, updateEffectSettings]);
  
  // Handle toggling a mapping's enabled state
  const handleToggleMapping = useCallback((mappingId: string, enabled: boolean) => {
    if (!selectedGame) return;
    
    const newSettings: GameEffectSettings = {
      mappings: effectSettings.mappings.map(m =>
        m.id === mappingId ? { ...m, enabled } : m
      ),
    };
    
    // Emit socket event to update settings
    updateEffectSettings({
      roomCode: selectedGame.roomCode,
      effectSettings: newSettings,
    });
  }, [effectSettings, selectedGame, updateEffectSettings]);
  
  // Handle updating a mapping's config
  const handleUpdateMappingConfig = useCallback((mappingId: string, config: Partial<BorderGlowEffectConfig | ConfettiEffectConfig | TsParticlesEffectConfig>) => {
    if (!selectedGame) return;
    
    const newSettings: GameEffectSettings = {
      mappings: effectSettings.mappings.map(m =>
        m.id === mappingId ? { ...m, config: { ...m.config, ...config } as typeof m.config } : m
      ),
    };
    
    // Emit socket event to update settings
    updateEffectSettings({
      roomCode: selectedGame.roomCode,
      effectSettings: newSettings,
    });
  }, [effectSettings, selectedGame, updateEffectSettings]);
  
  // Handle previewing a mapping's effect on a specific connected electron client
  const handlePreviewMapping = useCallback((mapping: EffectMapping) => {
    if (!selectedGame || !previewTargetPlayerId) return;
    const request: AdminPreviewEffectRequest = {
      roomCode: selectedGame.roomCode,
      effectType: mapping.effectType,
      config: mapping.config,
      targetPlayerId: previewTargetPlayerId,
    };
    previewEffect(request);
  }, [selectedGame, previewTargetPlayerId, previewEffect]);

  if (!selectedGame) {
    return (
      <div className="effects-settings text-white">
        <h2>✨ Visual Effects</h2>
        <p className="alert">Select a game from the dropdown above to configure effects.</p>
      </div>
    );
  }
  
  return (
    <div className="effects-settings text-white">
      <h2>✨ Visual Effects</h2>
      <p className="settings-description">
        Configure visual effects that trigger during game events. 
        Each event can have one effect assigned to it.
      </p>
      
      {/* Preview target — which connected player receives preview animations */}
      <PreviewTargetSelect players={selectedGame.players} value={previewTargetPlayerId} onChange={setPreviewTargetPlayerId} />

      {/* Existing Effect Mappings */}
      <div className="effect-mappings">
        <h3>Event to Effect Mappings</h3>
        
        {effectSettings.mappings.length === 0 ? (
          <div className="empty-mappings bg-space bg-o-50 border-md border-blue-light rounded-base p-sm text-center o-70">
            <p>No effect mappings configured. Add one below to get started!</p>
          </div>
        ) : (
          <div className="mappings-list">
            {effectSettings.mappings.map((mapping) => (
              <EffectMappingCard
                key={mapping.id}
                mapping={mapping}
                onToggle={(enabled) => handleToggleMapping(mapping.id, enabled)}
                onRemove={() => handleRemoveMapping(mapping.id)}
                onConfigChange={(config) => handleUpdateMappingConfig(mapping.id, config)}
                onPreview={() => handlePreviewMapping(mapping)}
                previewDisabled={!previewTargetPlayerId}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Add New Mapping */}
      <div className="add-mapping-section">
        {!addingMapping ? (
          <button 
            className="btn"
            onClick={() => setAddingMapping(true)}
            disabled={unusedEvents.length === 0}
          >
            + Add Effect Mapping
          </button>
        ) : (
          <div className="add-mapping-form bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mt-sm">
            <h4>Add New Effect Mapping</h4>
            
            <div className="form-row">
              <label>
                <span className="label-text">When this event occurs:</span>
                <select
                  className="select"
                  value={newMappingEvent}
                  onChange={(e) => setNewMappingEvent(e.target.value as EffectEvent)}
                >
                  <option value="">Select an event...</option>
                  {unusedEvents.map(event => (
                    <option key={event} value={event}>
                      {getEventIcon(event)} {getEventDisplayName(event)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="form-row">
              <label>
                <span className="label-text">Show this effect:</span>
                <select
                  className="select"
                  value={newMappingEffectType}
                  onChange={(e) => setNewMappingEffectType(e.target.value as EffectType)}
                >
                  {getAllEffectTypes().map(effectType => (
                    <option key={effectType} value={effectType}>
                      {getEffectTypeDisplayName(effectType)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn--outline btn--slate"
                onClick={() => {
                  setAddingMapping(false);
                  setNewMappingEvent('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn"
                onClick={handleAddMapping}
                disabled={!newMappingEvent}
              >
                Add Mapping
              </button>
            </div>
          </div>
        )}
        
        {unusedEvents.length === 0 && !addingMapping && (
          <p className="info-text">
            All events have effect mappings assigned. Remove a mapping to reassign an event.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Individual effect mapping card with config editing
 */
interface EffectMappingCardProps {
  mapping: EffectMapping;
  onToggle: (enabled: boolean) => void;
  onRemove: () => void;
  onConfigChange: (config: Partial<BorderGlowEffectConfig | ConfettiEffectConfig | TsParticlesEffectConfig>) => void;
  onPreview: () => void;
  previewDisabled?: boolean;
}

function EffectMappingCard({ mapping, onToggle, onRemove, onConfigChange, onPreview, previewDisabled }: EffectMappingCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = useCallback(() => {
    onPreview();
    setPreviewing(true);
    setTimeout(() => setPreviewing(false), 1500);
  }, [onPreview]);
  
  return (
    <div className={`effect-mapping-card bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mb-xs d-flex flex-column gap-xs text-white${mapping.enabled ? '' : ' o-50'}`}>
      <div className="d-flex justify-content-between align-items-center gap-sm">
        <div className="d-flex align-items-center gap-xs">
          <span className="event-icon fs-xl">{getEventIcon(mapping.event)}</span>
          <div className="d-flex flex-column">
            <span className="fw-bold">{getEventDisplayName(mapping.event)}</span>
            <span className="fs-sm o-70">→ {getEffectTypeDisplayName(mapping.effectType)}</span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-xs">
          <button
            className="btn btn--outline btn--slate btn--sm"
            onClick={handlePreview}
            disabled={previewing || previewDisabled}
            title={previewDisabled ? 'Select a player above to enable preview' : 'Preview effect on selected client'}
          >
            {previewing ? '...' : 'Preview'}
          </button>
          <button
            className="btn btn--outline btn--slate btn--sm"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Collapse settings' : 'Expand settings'}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          <label className="check" title={mapping.enabled ? 'Disable' : 'Enable'}>
            <input
              className="switch"
              type="checkbox"
              checked={mapping.enabled}
              onChange={(e) => onToggle(e.target.checked)}
            />
          </label>
          <button
            className="btn btn--red btn--sm"
            onClick={onRemove}
            title="Remove mapping"
          >
            Remove
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mapping-config mt-xs pt-xs border-top border-md border-blue-light">
          {mapping.effectType === EffectType.BORDER_GLOW && (
            <BorderGlowConfigEditor
              config={mapping.config as BorderGlowEffectConfig}
              onChange={onConfigChange}
            />
          )}
          {mapping.effectType === EffectType.CONFETTI_DEPRECATED && (
            <ConfettiConfigEditor
              config={mapping.config as ConfettiEffectConfig}
              onChange={onConfigChange}
            />
          )}
          {isTsParticlesEffectType(mapping.effectType) && (
            <TsParticlesConfigEditor
              config={mapping.config as TsParticlesEffectConfig}
              onChange={onConfigChange}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Border Glow effect configuration editor
 */
interface BorderGlowConfigEditorProps {
  config: BorderGlowEffectConfig;
  onChange: (config: Partial<BorderGlowEffectConfig>) => void;
}

function BorderGlowConfigEditor({ config, onChange }: BorderGlowConfigEditorProps) {
  return (
    <div className="effect-config-grid">
      <div className="config-item">
        <label>
          <span>Color</span>
          <div className="color-picker-wrapper">
            <input
              type="color"
              value={config.color}
              onChange={(e) => onChange({ color: e.target.value })}
            />
            <span className="color-value">{config.color}</span>
          </div>
        </label>
      </div>
      
      <div className="config-item">
        <label>
          <span>Duration ({(config.durationMs / 1000).toFixed(1)}s)</span>
          <input
            className="range"
            type="range"
            min={1000}
            max={10000}
            step={500}
            value={config.durationMs}
            onChange={(e) => onChange({ durationMs: parseInt(e.target.value) })}
          />
        </label>
      </div>
      
      <div className="config-item">
        <label>
          <span>Intensity ({Math.round(config.intensity * 100)}%)</span>
          <input
            className="range"
            type="range"
            min={0.1}
            max={1}
            step={0.1}
            value={config.intensity}
            onChange={(e) => onChange({ intensity: parseFloat(e.target.value) })}
          />
        </label>
      </div>
      
      <div className="config-item">
        <label>
          <span>Border Width ({config.borderWidth}px)</span>
          <input
            className="range"
            type="range"
            min={10}
            max={50}
            step={5}
            value={config.borderWidth}
            onChange={(e) => onChange({ borderWidth: parseInt(e.target.value) })}
          />
        </label>
      </div>
      
      <div className="config-item">
        <label className="toggle-label">
          <span>Animated (Pulsing)</span>
          <input
            className="checkbox"
            type="checkbox"
            checked={config.animated}
            onChange={(e) => onChange({ animated: e.target.checked })}
          />
        </label>
      </div>
    </div>
  );
}

/**
 * Confetti effect configuration editor
 */
interface ConfettiConfigEditorProps {
  config: ConfettiEffectConfig;
  onChange: (config: Partial<ConfettiEffectConfig>) => void;
}

function ConfettiConfigEditor({ config, onChange }: ConfettiConfigEditorProps) {
  const presets: ConfettiPreset[] = ['fireworks', 'realistic', 'explosion', 'pride', 'vortex', 'custom'];
  
  return (
    <div className="effect-config-grid confetti-config">
      {/* Preset Selection */}
      <div className="config-item full-width">
        <label>
          <span>Preset Style</span>
          <select
            className="select"
            value={config.preset}
            onChange={(e) => onChange({ preset: e.target.value as ConfettiPreset })}
          >
            {presets.map(preset => (
              <option key={preset} value={preset}>
                {preset.charAt(0).toUpperCase() + preset.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <p className="config-hint">
          {config.preset === 'fireworks' && 'Bursts from random positions across the screen'}
          {config.preset === 'realistic' && 'Multiple waves with varying parameters'}
          {config.preset === 'explosion' && 'Burst from center with stars and circles'}
          {config.preset === 'pride' && 'Colorful streams from both sides'}
          {config.preset === 'vortex' && 'Rotating spiral pattern'}
          {config.preset === 'custom' && 'Use the settings below for full control'}
        </p>
      </div>
      
      {/* Duration */}
      <div className="config-item">
        <label>
          <span>Duration ({(config.durationMs / 1000).toFixed(1)}s)</span>
          <input
            className="range"
            type="range"
            min={1000}
            max={15000}
            step={1000}
            value={config.durationMs}
            onChange={(e) => onChange({ durationMs: parseInt(e.target.value) })}
          />
        </label>
      </div>
      
      {/* Particle Count */}
      <div className="config-item">
        <label>
          <span>Particle Count ({config.particleCount})</span>
          <input
            className="range"
            type="range"
            min={10}
            max={200}
            step={10}
            value={config.particleCount}
            onChange={(e) => onChange({ particleCount: parseInt(e.target.value) })}
          />
        </label>
      </div>
      
      {/* Advanced settings (collapsible) */}
      <details className="config-item full-width">
        <summary>Advanced Settings</summary>
        <div className="advanced-settings">
          <div className="config-item">
            <label>
              <span>Spread ({config.spread}°)</span>
              <input
                className="range"
                type="range"
                min={0}
                max={360}
                step={15}
                value={config.spread}
                onChange={(e) => onChange({ spread: parseInt(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Launch Angle ({config.angle}°)</span>
              <input
                className="range"
                type="range"
                min={0}
                max={360}
                step={15}
                value={config.angle}
                onChange={(e) => onChange({ angle: parseInt(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Start Velocity ({config.startVelocity})</span>
              <input
                className="range"
                type="range"
                min={10}
                max={100}
                step={5}
                value={config.startVelocity}
                onChange={(e) => onChange({ startVelocity: parseInt(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Gravity ({config.gravity})</span>
              <input
                className="range"
                type="range"
                min={0}
                max={3}
                step={0.1}
                value={config.gravity}
                onChange={(e) => onChange({ gravity: parseFloat(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Decay ({config.decay})</span>
              <input
                className="range"
                type="range"
                min={0.5}
                max={1}
                step={0.05}
                value={config.decay}
                onChange={(e) => onChange({ decay: parseFloat(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Drift ({config.drift})</span>
              <input
                className="range"
                type="range"
                min={-3}
                max={3}
                step={0.5}
                value={config.drift}
                onChange={(e) => onChange({ drift: parseFloat(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Ticks ({config.ticks})</span>
              <input
                className="range"
                type="range"
                min={50}
                max={500}
                step={50}
                value={config.ticks}
                onChange={(e) => onChange({ ticks: parseInt(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <span>Scale ({config.scalar})</span>
              <input
                className="range"
                type="range"
                min={0.5}
                max={3}
                step={0.1}
                value={config.scalar}
                onChange={(e) => onChange({ scalar: parseFloat(e.target.value) })}
              />
            </label>
          </div>
          
          <div className="config-item">
            <label className="toggle-label">
              <span>Flat (No 3D)</span>
              <input
                className="checkbox"
                type="checkbox"
                checked={config.flat}
                onChange={(e) => onChange({ flat: e.target.checked })}
              />
            </label>
          </div>
        </div>
      </details>
    </div>
  );
}

/**
 * tsParticles effect configuration editor.
 * All fields are optional overrides — leaving a control at its reset state
 * tells the renderer to use the preset's built-in value for that property.
 */
interface TsParticlesConfigEditorProps {
  config: TsParticlesEffectConfig;
  onChange: (config: Partial<TsParticlesEffectConfig>) => void;
}

function TsParticlesConfigEditor({ config, onChange }: TsParticlesConfigEditorProps) {
  const [newColor, setNewColor] = useState('#ff0000');
  const colors = config.colors ?? [];

  const handleAddColor = () => {
    if (!colors.includes(newColor)) {
      onChange({ colors: [...colors, newColor] });
    }
  };

  const handleRemoveColor = (hex: string) => {
    const next = colors.filter(c => c !== hex);
    onChange({ colors: next.length > 0 ? next : undefined });
  };

  const handleUpdateColor = (oldHex: string, newHex: string) => {
    onChange({ colors: colors.map(c => (c === oldHex ? newHex : c)) });
  };

  const handleResetColors = () => onChange({ colors: undefined });

  return (
    <div className="effect-config-grid">
      {/* Duration */}
      <div className="config-item">
        <label>
          <span>Duration ({(config.durationMs / 1000).toFixed(1)}s)</span>
          <input
            className="range"
            type="range"
            min={1000}
            max={15000}
            step={500}
            value={config.durationMs}
            onChange={(e) => onChange({ durationMs: parseInt(e.target.value) })}
          />
        </label>
      </div>

      {/* Speed */}
      <div className="config-item">
        <label>
          <span>
            Speed {config.speed !== undefined ? `(${config.speed.toFixed(1)}×)` : '(preset default)'}
          </span>
          <input
            className="range"
            type="range"
            min={0.1}
            max={5}
            step={0.1}
            value={config.speed ?? 1}
            onChange={(e) => onChange({ speed: parseFloat(e.target.value) })}
          />
        </label>
        {config.speed !== undefined && (
          <button className="btn-reset" onClick={() => onChange({ speed: undefined })}>Reset</button>
        )}
      </div>

      {/* Opacity */}
      <div className="config-item">
        <label>
          <span>
            Opacity {config.opacity !== undefined ? `(${Math.round(config.opacity * 100)}%)` : '(preset default)'}
          </span>
          <input
            className="range"
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={config.opacity ?? 0.8}
            onChange={(e) => onChange({ opacity: parseFloat(e.target.value) })}
          />
        </label>
        {config.opacity !== undefined && (
          <button className="btn-reset" onClick={() => onChange({ opacity: undefined })}>Reset</button>
        )}
      </div>

      {/* Size */}
      <div className="config-item">
        <label>
          <span>
            Particle Size {config.size !== undefined ? `(${config.size}px)` : '(preset default)'}
          </span>
          <input
            className="range"
            type="range"
            min={1}
            max={30}
            step={1}
            value={config.size ?? 5}
            onChange={(e) => onChange({ size: parseInt(e.target.value) })}
          />
        </label>
        {config.size !== undefined && (
          <button className="btn-reset" onClick={() => onChange({ size: undefined })}>Reset</button>
        )}
      </div>

      {/* Particle Count */}
      <div className="config-item">
        <label>
          <span>
            Particle Count {config.particleCount !== undefined ? `(${config.particleCount})` : '(preset default)'}
          </span>
          <input
            className="range"
            type="range"
            min={10}
            max={500}
            step={10}
            value={config.particleCount ?? 100}
            onChange={(e) => onChange({ particleCount: parseInt(e.target.value) })}
          />
        </label>
        {config.particleCount !== undefined && (
          <button className="btn-reset" onClick={() => onChange({ particleCount: undefined })}>Reset</button>
        )}
      </div>

      {/* Colors */}
      <div className="config-item full-width">
        <span>Colors {colors.length === 0 ? '(preset default)' : `(${colors.length} custom)`}</span>
        <div className="color-list">
          {colors.map((hex) => (
            <div key={hex} className="color-swatch-row">
              <input
                type="color"
                value={hex}
                onChange={(e) => handleUpdateColor(hex, e.target.value)}
              />
              <span className="color-value">{hex}</span>
              <button className="btn-reset" onClick={() => handleRemoveColor(hex)}>✕</button>
            </div>
          ))}
          <div className="color-swatch-row">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
            />
            <button className="btn btn--outline btn--slate" onClick={handleAddColor}>Add Color</button>
            {colors.length > 0 && (
              <button className="btn-reset" onClick={handleResetColors}>Reset All</button>
            )}
          </div>
        </div>
        <p className="config-hint">Leave empty to use the preset&apos;s built-in color scheme</p>
      </div>
    </div>
  );
}
